import { apiUrls } from './ApiUrls';
import { BaseApiService } from './BaseApiService';
import { ApiResponse } from './ApiResponse';

export interface PartnerEnquiryFormValues {
  organizationName: string;
  contactPerson: string;
  email: string;
  phone: string;
  location: string;
  partnershipType: string;
  message: string;
}

export interface PartnerEnquiryResponse {
  id: number;
  enquiryNumber: string;
  organizationName: string;
  contactPerson: string;
  email: string;
  phone: string;
  location: string;
  partnershipType: string;
  message: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Service class for partner enquiry-related API operations
 * Connects to the backend PartnerEnquiryController endpoints
 */
class PartnerEnquiryService extends BaseApiService {
  private static instance: PartnerEnquiryService;

  public static getInstance(): PartnerEnquiryService {
    if (!PartnerEnquiryService.instance) {
      PartnerEnquiryService.instance = new PartnerEnquiryService();
    }
    return PartnerEnquiryService.instance;
  }

  /**
   * Submit partner enquiry form (public endpoint, no authentication required)
   */
  static async submitPartnerEnquiry(
    formData: PartnerEnquiryFormValues
  ): Promise<ApiResponse<PartnerEnquiryResponse>> {
    try {
      console.log('📝 [PartnerEnquiryService] Submitting partner enquiry:', formData.organizationName);
      const response = await this.post(
        apiUrls.partnerEnquiry.submit,
        formData,
        'Submit Partner Enquiry'
      );
      return response;
    } catch (error: any) {
      console.error('❌ [PartnerEnquiryService] Error submitting partner enquiry:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to submit partner enquiry',
      };
    }
  }

  /**
   * Get all partner enquiries (admin only)
   */
  static async getAllPartnerEnquiries(): Promise<ApiResponse<PartnerEnquiryResponse[]>> {
    try {
      console.log('📋 [PartnerEnquiryService] Fetching all partner enquiries...');
      const response = await this.getAuthenticated(
        apiUrls.partnerEnquiry.list,
        'Get All Partner Enquiries'
      );
      return response;
    } catch (error: any) {
      console.error('❌ [PartnerEnquiryService] Error fetching partner enquiries:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch partner enquiries',
      };
    }
  }

  /**
   * Get partner enquiries by status (admin only)
   */
  static async getPartnerEnquiriesByStatus(
    status: string
  ): Promise<ApiResponse<PartnerEnquiryResponse[]>> {
    try {
      console.log('📋 [PartnerEnquiryService] Fetching partner enquiries by status:', status);
      const response = await this.getAuthenticated(
        apiUrls.partnerEnquiry.getByStatus(status),
        'Get Partner Enquiries by Status'
      );
      return response;
    } catch (error: any) {
      console.error('❌ [PartnerEnquiryService] Error fetching partner enquiries by status:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch partner enquiries by status',
      };
    }
  }

  /**
   * Get partner enquiry by ID (admin only)
   */
  static async getPartnerEnquiryById(
    id: number
  ): Promise<ApiResponse<PartnerEnquiryResponse>> {
    try {
      console.log('📋 [PartnerEnquiryService] Fetching partner enquiry by ID:', id);
      const response = await this.getAuthenticated(
        apiUrls.partnerEnquiry.getById(id),
        'Get Partner Enquiry by ID'
      );
      return response;
    } catch (error: any) {
      console.error('❌ [PartnerEnquiryService] Error fetching partner enquiry by ID:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch partner enquiry',
      };
    }
  }
}

// Export the class for static method access
export { PartnerEnquiryService };
export default PartnerEnquiryService;
